// Use same server as login; in dev with proxy (package.json "proxy": "http://localhost:8080") use relative URL so request goes to local server + stackers_db
function getApiBase() {
  const localUrl = process.env.REACT_APP_LOCAL_SERVER_URL;
  const isBrowser = typeof window !== 'undefined';
  const isLocalBrowser =
    isBrowser &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  if (isLocalBrowser && localUrl) return localUrl;
  if (process.env.REACT_APP_SERVER_URL !== undefined && process.env.REACT_APP_SERVER_URL !== '') return process.env.REACT_APP_SERVER_URL;
  if (localUrl !== undefined && localUrl !== '') return localUrl;
  if (process.env.NODE_ENV === 'development') return ''; // relative URL => proxied to localhost:8080
  const loginUrl = process.env.REACT_APP_LOGIN_URL;
  if (loginUrl) return loginUrl.replace(/\/login\/?$/, '');
  return '';
}
const STACKERS_API_BASE = getApiBase();

export function getWebhookUrl(path = '') {
  return `${STACKERS_API_BASE}/webhook${path ? `/${path}` : ''}`;
}

export async function fetchProjectsSummary(token) {
  const url = `${STACKERS_API_BASE}/webhook/projects/summary`;
  const controller = new AbortController();
  const timeoutMs = 30000;
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  let res;
  try {
    res = await fetch(url, {
      headers: token ? { authorization: `Bearer ${token}` } : {},
      signal: controller.signal,
    });
  } catch (networkErr) {
    if (networkErr.name === 'AbortError') {
      throw new Error(`Timed out loading stacker projects after ${Math.round(timeoutMs / 1000)}s`);
    }
    const msg = networkErr.message || 'Network error';
    throw new Error(
      msg.includes('fetch') ? `Cannot reach server. Is it running on port 8080? (${msg})` : msg
    );
  } finally {
    clearTimeout(timeoutId);
  }
  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch (_) {
    throw new Error(`Server error ${res.status}: ${text.slice(0, 100)}`);
  }
  if (!res.ok) {
    const errMsg = data.error || data.message || text || res.statusText;
    throw new Error(errMsg || `Failed to fetch projects summary (${res.status})`);
  }
  return data.projects || [];
}

export async function fetchProjectReports(projectName, token, limit = 20) {
  const encoded = encodeURIComponent(projectName);
  const res = await fetch(`${STACKERS_API_BASE}/webhook/${encoded}/reports?limit=${limit}`, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('Failed to fetch project reports');
  const data = await res.json();
  return data.reports || [];
}

export async function fetchProjectPhotos(projectName, token) {
  const encoded = encodeURIComponent(projectName);
  const res = await fetch(`${STACKERS_API_BASE}/webhook/${encoded}/photos`, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('Failed to fetch project photos');
  const data = await res.json();
  return data.photos || [];
}

export async function fetchProjectProgress(projectName, token) {
  const encoded = encodeURIComponent(projectName);
  const res = await fetch(`${STACKERS_API_BASE}/webhook/${encoded}`, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('Failed to fetch project progress');
  return res.json();
}

/** URL for latest daily report HTML (same as email PDF content) */
export function getLatestReportHtmlUrl(projectName) {
  const encoded = encodeURIComponent(projectName);
  return `${STACKERS_API_BASE}/webhook/${encoded}/reports/latest/html`;
}

/** URL for latest daily report PDF download */
export function getLatestReportPdfUrl(projectName) {
  const encoded = encodeURIComponent(projectName);
  return `${STACKERS_API_BASE}/webhook/${encoded}/reports/latest/pdf`;
}

/** Fetch latest daily report HTML (same as email); returns { html } or throws */
export async function fetchLatestReportHtml(projectName, token) {
  const url = getLatestReportHtmlUrl(projectName);
  const res = await fetch(url, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  const text = await res.text();
  if (!res.ok) {
    const msg = text || res.statusText || `Request failed (${res.status})`;
    throw new Error(msg);
  }
  return { html: text };
}

/** Fetch latest daily report PDF blob for download */
export async function fetchLatestReportPdfBlob(projectName, token) {
  const url = getLatestReportPdfUrl(projectName);
  const res = await fetch(url, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const msg = await res.text();
    throw new Error(msg || res.statusText || `Request failed (${res.status})`);
  }
  return res.blob();
}

/** Fetch completion % over time (by report date) for line chart; returns { history: [{ report_date, completion_percent }] } */
export async function fetchCompletionHistory(projectName, token) {
  const encoded = encodeURIComponent(projectName);
  const res = await fetch(`${STACKERS_API_BASE}/webhook/${encoded}/completion-history`, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('Failed to fetch completion history');
  const data = await res.json();
  return data.history || [];
}

/** Fetch a specific daily report by ID as HTML (full report with images and signature); returns { html } or throws */
export async function fetchReportHtmlById(projectName, reportId, token) {
  const encoded = encodeURIComponent(projectName);
  const url = `${STACKERS_API_BASE}/webhook/${encoded}/reports/${reportId}/html`;
  const res = await fetch(url, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  const text = await res.text();
  if (!res.ok) {
    const msg = text || res.statusText || `Request failed (${res.status})`;
    throw new Error(msg);
  }
  return { html: text };
}

/** Fetch a specific daily report PDF blob by report ID */
export async function fetchReportPdfBlobById(projectName, reportId, token) {
  const encoded = encodeURIComponent(projectName);
  const url = `${STACKERS_API_BASE}/webhook/${encoded}/reports/${reportId}/pdf`;
  const res = await fetch(url, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const msg = await res.text();
    throw new Error(msg || res.statusText || `Request failed (${res.status})`);
  }
  return res.blob();
}

function authHeaders(token) {
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function parseJsonOrThrow(res, fallbackMessage) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || data.message || fallbackMessage);
  }
  return data;
}

export async function fetchStackerTypes(token) {
  const res = await fetch(`${STACKERS_API_BASE}/admin/stackers/types`, {
    headers: authHeaders(token),
  });
  const data = await parseJsonOrThrow(res, "Failed to fetch stacker types");
  return data.types || [];
}

export async function createStackerType(payload, token) {
  const res = await fetch(`${STACKERS_API_BASE}/admin/stackers/types`, {
    method: "POST",
    headers: { "content-type": "application/json", ...authHeaders(token) },
    body: JSON.stringify(payload || {}),
  });
  return parseJsonOrThrow(res, "Failed to create stacker type");
}

export async function createStackerTypeVersion(typeId, payload, token) {
  const res = await fetch(`${STACKERS_API_BASE}/admin/stackers/types/${typeId}/version`, {
    method: "POST",
    headers: { "content-type": "application/json", ...authHeaders(token) },
    body: JSON.stringify(payload || {}),
  });
  return parseJsonOrThrow(res, "Failed to create stacker type version");
}

export async function saveStackerProjectSetup(payload, token) {
  const res = await fetch(`${STACKERS_API_BASE}/admin/stackers/project-setup`, {
    method: "POST",
    headers: { "content-type": "application/json", ...authHeaders(token) },
    body: JSON.stringify(payload || {}),
  });
  return parseJsonOrThrow(res, "Failed to save stacker project setup");
}

export async function fetchStackerProjectSetup(projectName, token) {
  const encoded = encodeURIComponent(projectName);
  const res = await fetch(`${STACKERS_API_BASE}/admin/stackers/project-setup/${encoded}`, {
    headers: authHeaders(token),
  });
  return parseJsonOrThrow(res, "Failed to fetch stacker project setup");
}

export async function fetchStackerProjectsList(token) {
  const res = await fetch(`${STACKERS_API_BASE}/admin/stackers/projects`, {
    headers: authHeaders(token),
  });
  const data = await parseJsonOrThrow(res, "Failed to fetch stacker projects list");
  return data.projects || [];
}

export async function setStackerProjectVisibility(projectId, hidden, token) {
  const res = await fetch(`${STACKERS_API_BASE}/admin/stackers/projects/${projectId}/visibility`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
    body: JSON.stringify({ hidden }),
  });
  return parseJsonOrThrow(res, 'Failed to update project visibility');
}
